import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { TaskCard } from '../service';
import { formatCurrency } from '../../utils';
import type { ServiceTask } from '../../types/proposal';
import type { TaskDecision } from '../../App';

interface TaskSectionProps {
  taskDecisions: Record<string, TaskDecision>;
  onTaskDecisionChange: React.Dispatch<React.SetStateAction<Record<string, TaskDecision>>>;
}

export function TaskSection({ taskDecisions, onTaskDecisionChange }: TaskSectionProps) {
  const proposal = useProposalData();

  // Only show for service proposals with tasks
  if (proposal.proposalType !== 'service' || !proposal.tasks || proposal.tasks.length === 0) {
    return null;
  }

  const showLineDetails = proposal.showLineDetails ?? false;

  // Merge task data with local decisions
  const tasksWithDecisions: ServiceTask[] = proposal.tasks.map(task => ({
    ...task,
    accepted: taskDecisions[task.lineId]?.accepted ?? task.accepted,
    declined: taskDecisions[task.lineId]?.declined ?? task.declined,
    declinedReason: taskDecisions[task.lineId]?.reason ?? task.declinedReason,
  }));

  // Calculate summary stats
  const acceptedTasks = tasksWithDecisions.filter(t => t.accepted);
  const declinedTasks = tasksWithDecisions.filter(t => t.declined);
  const pendingTasks = tasksWithDecisions.filter(t => !t.accepted && !t.declined);

  const acceptedTotal = acceptedTasks.reduce((sum, t) => sum + t.price, 0);
  const declinedTotal = declinedTasks.reduce((sum, t) => sum + t.price, 0);
  const pendingTotal = pendingTasks.reduce((sum, t) => sum + t.price, 0);
  const totalEstimate = tasksWithDecisions.reduce((sum, t) => sum + t.price, 0);

  const handleAccept = useCallback((lineId: string) => {
    onTaskDecisionChange(prev => ({
      ...prev,
      [lineId]: { accepted: true, declined: false, reason: undefined },
    }));
  }, [onTaskDecisionChange]);

  const handleDecline = useCallback((lineId: string, reason?: string) => {
    onTaskDecisionChange(prev => ({
      ...prev,
      [lineId]: { accepted: false, declined: true, reason },
    }));
  }, [onTaskDecisionChange]);

  return (
    <Section id="tasks" className="bg-white">
      {/* Summary Bar */}
      <motion.div
        className="mb-12 -mt-8 p-6 bg-hul-navy rounded-xl shadow-lg"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Total Estimate */}
          <div className="text-center md:text-left">
            <div className="text-white/60 text-xs uppercase tracking-wider mb-1">
              Total Estimate
            </div>
            <div className="text-white font-mono text-2xl font-bold">
              {formatCurrency(totalEstimate)}
            </div>
            <div className="text-white/50 text-sm">
              {proposal.tasks.length} task{proposal.tasks.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Accepted */}
          <div className="text-center md:text-left">
            <div className="flex items-center gap-2 text-green-400 text-xs uppercase tracking-wider mb-1 justify-center md:justify-start">
              <CheckCircle2 className="w-4 h-4" />
              Accepted
            </div>
            <div className="text-green-400 font-mono text-xl font-bold">
              {formatCurrency(acceptedTotal)}
            </div>
            <div className="text-white/50 text-sm">
              {acceptedTasks.length} task{acceptedTasks.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Pending */}
          <div className="text-center md:text-left">
            <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-wider mb-1 justify-center md:justify-start">
              <Clock className="w-4 h-4" />
              Pending
            </div>
            <div className="text-amber-400 font-mono text-xl font-bold">
              {formatCurrency(pendingTotal)}
            </div>
            <div className="text-white/50 text-sm">
              {pendingTasks.length} task{pendingTasks.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Declined */}
          <div className="text-center md:text-left">
            <div className="flex items-center gap-2 text-red-400 text-xs uppercase tracking-wider mb-1 justify-center md:justify-start">
              <XCircle className="w-4 h-4" />
              Declined
            </div>
            <div className="text-red-400 font-mono text-xl font-bold">
              {formatCurrency(declinedTotal)}
            </div>
            <div className="text-white/50 text-sm">
              {declinedTasks.length} task{declinedTasks.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </motion.div>

      <SectionHeader
        title="SERVICE TASKS"
        subtitle="Review & Approve"
      />

      {/* Instructions */}
      <motion.div
        className="mb-8 p-4 bg-blue-50 rounded-xl border border-blue-100"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="flex items-start gap-3">
          <ClipboardList className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-heading text-blue-900 uppercase tracking-wider text-sm mb-1">
              How to Review
            </h4>
            <p className="text-blue-700 text-sm">
              Review each task below and click <strong>Accept</strong> to approve the work or <strong>Decline</strong> if you'd like to skip it.
              You can provide a reason when declining to help us understand your needs.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Task Cards */}
      <div className="space-y-6">
        {tasksWithDecisions.map((task, index) => (
          <TaskCard
            key={task.lineId}
            task={task}
            index={index}
            showLineDetails={showLineDetails}
            onAccept={handleAccept}
            onDecline={handleDecline}
          />
        ))}
      </div>

      {/* Bottom Summary */}
      {(acceptedTasks.length > 0 || declinedTasks.length > 0) && (
        <motion.div
          className="mt-12 p-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <h4 className="font-heading text-hul-navy uppercase tracking-wider text-lg mb-2">
              Your Selections
            </h4>
            <p className="text-gray-600 mb-4">
              You've reviewed {acceptedTasks.length + declinedTasks.length} of {proposal.tasks.length} tasks
            </p>

            {pendingTasks.length > 0 ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm">
                <Clock className="w-4 h-4" />
                {pendingTasks.length} task{pendingTasks.length !== 1 ? 's' : ''} still pending
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm">
                <CheckCircle2 className="w-4 h-4" />
                All tasks reviewed!
              </div>
            )}
          </div>
        </motion.div>
      )}
    </Section>
  );
}
